<template>
  <div>
    <h3 v-if="person.isAdult" class="weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter Your Residence Details
    </h3>

    <div class="flex justify-md-center align-center pb-10">
      <h4 class="weight-medium text-left pr-6">
        Permanent Residence Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    Your <b>permanent residence address</b> is the place in the Slovak Republic where you are staying long-term.
    You can only have one permanent residence address, and it must be stated on your ID card.
    <br />
    <br />
    In the case of a child, the permanent residence address is the same as the permanent residence
    address of the legal representative.
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <div v-if="!person.isAdult && adults.length"
         class="input-box-holder"
         v-validate="errors"
         v-has-value="person.addressSameAsPersonWithIndex"
    >
      <div class="validation-output text-left mb-5">
        <ErrorHandler class="error" :id="`V226_${personIndex}`">
          You have not selected any of the options. To continue, please select one option.
        </ErrorHandler>
      </div>
      <div class="block">
        <RadioButton
          v-for="(adultObject, index) in adultsWithIndexes"
          :key="index"
          class="body-3 weight-regular my-5"
          :name="'addressSameAsPersonWithIndex' + personIndex"
          :option="index"
          :value="person.addressSameAsPersonWithIndex"
          @input="setAddressSameAsPersonWithIndex"
        >
          <template v-slot:title>
            Same as {{ adultObject.adult.name }} {{ adultObject.adult.surname }}
          </template>
        </RadioButton>
        <RadioButton
          class="body-3 weight-regular my-5"
          title='Different'
          :name="'addressSameAsPersonWithIndex' + personIndex"
          :option="-1"
          :value="person.addressSameAsPersonWithIndex"
          @input="setAddressSameAsPersonWithIndex"
        />
      </div>
    </div>

    <PermanentAddress
      v-if="showPermanentAddress"
      :address="person.address"
      :errors="errors"
      :index="personIndex"
    />

    <div class="flex justify-md-center align-center pt-16 pb-10">
      <h4 class="weight-medium text-left pr-6">
        Mailing Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>The mailing address</b> or delivery address is the place in the Slovak Republic where you
    wish to receive all written documents.
    <br />
    <br />
    Please enter the mailing address if you wish to receive documents at an address different from
    your permanent residence address.
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <Checkbox class="cb-card heading-4 weight-medium"
              title='Different from Your Permanent Residence Address'
              @input="setPostallAddresSame"
              :value="!person.postalAddressSameAsAddress"
              :checked="!person.postalAddressSameAsAddress"
    >
      <PostalAddress v-if="!person.postalAddressSameAsAddress" :person="person" :address="person.postalAddress"
        :errors="errors" :index="personIndex" />
    </Checkbox>

    <div class="flex justify-md-center align-center pt-16 pb-10">
      <h4 class="weight-medium text-left pr-6">
        Temporary Residence Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>A temporary residence address</b> is a place in the Slovak Republic different from your permanent
    residence address where you are staying for more than 90 days a year.
    <br />
    <br />
    Enter a temporary residence address if:
    <ul class="pl-8">
        <li>
            you do not regularly stay at your permanent residence address
        </li>
        <li>
            you are a citizen with a permanent residence abroad and your temporary stay in the territory
            of the Slovak Republic is longer than 90 days
        </li>
    </ul>
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <Checkbox v-if="person.isAdult"
              class="cb-card heading-4 weight-medium mb-10"
              title='The Policyholder Has a Temporary Residence'
              @input="setTemporaryResidenceSame"
              :value="person.hasTemporaryResidence"
              :checked="person.hasTemporaryResidence"
    >
      <TemporaryAddress v-if="person.hasTemporaryResidence" :address="person.temporaryResidence"
        :errors="errors" :index="personIndex" />
    </Checkbox>
    <Checkbox v-else
              class="cb-card heading-4 weight-medium mb-10"
              title='The Child Has a Temporary Residence'
              @input="setTemporaryResidenceSame"
              :value="person.hasTemporaryResidence"
              :checked="person.hasTemporaryResidence"
    >
      <TemporaryAddress v-if="person.hasTemporaryResidence" :address="person.temporaryResidence"
                        :errors="errors" :index="personIndex" />
    </Checkbox>
  </div>
</template>
<script>
import Checkbox from '@/components/Checkbox/Checkbox.vue';
import PermanentAddress from '@/pagesComponents/AddressComponents/PermanentAddress.vue';
import PostalAddress from '@/pagesComponents/AddressComponents/PostalAddress.vue';
import TemporaryAddress from '@/pagesComponents/AddressComponents/TemporaryAddress.vue';
import { COUNTRY_CODE_FOR_SLOVAKIA } from '@/config';
import persons from '@/mixins/persons';
import address from '@/mixins/address';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import RadioButton from '@/components/RadioButton/RadioButton.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import { addressIsNotFullyFilled } from '../utils/address';

export default {
  components: {
    ErrorHandler,
    RadioButton,
    PermanentAddress,
    PostalAddress,
    TemporaryAddress,
    Checkbox,
    Tooltip,
  },
  mixins: [persons, address],
  props: {
    person: { type: Object, required: true },
    model: { type: Object, required: true },
    errors: { type: Array, required: true },
    personIndex: { type: Number, required: true },
  },
  mounted() {
    if (this.personIndex === 0 || this.adults.length === 0) this.person.addressSameAsPersonWithIndex = -1;
  },
  computed: {
    insuredPersons() {
      return this.model.insuredPersons.array_hd7ov6$_0;
    },
    showPermanentAddress() {
      return this.person.isAdult || this.person.addressSameAsPersonWithIndex === -1 || this.adults.length === 0;
    },
  },
  methods: {
    setPostallAddresSame(value) {
      this.person.postalAddressSameAsAddress = !value;
      if (!value) {
        const { Address_init } = this.$kotlin.model;
        this.person.postalAddress = Address_init();
        this.person.postalAddress.country = this.$store.getters.getSlovakia;
      }
    },
    setTemporaryResidenceSame(value) {
      this.person.hasTemporaryResidence = value;
      if (value && addressIsNotFullyFilled(this.person.postalAddress)) {
        this.person.postalAddressSameAsAddress = true;
      }
      if (value && this.person.address?.country?.value !== COUNTRY_CODE_FOR_SLOVAKIA) {
        this.person.postalAddressSameAsAddress = false;
      }
      if (!value) {
        const { Address_init } = this.$kotlin.model;
        this.person.temporaryResidence = Address_init();
        this.person.temporaryResidence.country = this.$store.getters.getSlovakia;
      }
    },
    createNewAddress() {
      return new this.$kotlin.model.Address(
        this.$store.getters.getSlovakia,
        null, null, null, null,
      );
    },
    setAddressSameAsPersonWithIndex(personIndex) {
      if (personIndex === -1) {
        this.person.address = this.createNewAddress();
      } else {
        this.person.address = this.insuredPersons[personIndex].address;
      }
      this.person.addressSameAsPersonWithIndex = personIndex;
    },
    getAddressSameAsPersonWithIndexTitle(name, surname) {
      return `Same as ${name} ${surname}`;
    },
  },
  watch: {
    'person.address.country': {
      handler() {
        const { country } = this.person.address;

        if (country?.value && country?.value !== COUNTRY_CODE_FOR_SLOVAKIA) {
          this.person.postalAddressSameAsAddress = false;

          const refPersons = this.insuredPersons
            .filter((_, index) => index !== this.personIndex) // exclude current person
            .filter((person) => person.addressSameAsPersonWithIndex === this.personIndex); // filter persons who use address of current person

          refPersons.forEach((person) => {
            person.postalAddressSameAsAddress = false;
          });
        }
      },
      immediate: true,
    },
  },
};
</script>
